import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Video, Upload, Sparkles, Loader2, CheckCircle, XCircle, Image as ImageIcon, FileVideo, Trash2, Eye } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const POLL_INTERVAL_MS = 5000;

export default function AdminVideoGenerator() {
  const navigate = useNavigate();
  const [mode, setMode] = useState('generate'); // 'generate' | 'upload'
  const [title, setTitle] = useState('');
  const [topic, setTopic] = useState('');
  const [prompt, setPrompt] = useState('');
  const [comicPreview, setComicPreview] = useState('');   // data URL for preview
  const [comicBase64, setComicBase64] = useState('');     // raw base64 to send
  const [comicUrl, setComicUrl] = useState('');           // fallback URL input
  const [videoFile, setVideoFile] = useState(null);       // MP4 file for upload mode
  const [videoFileName, setVideoFileName] = useState('');
  const [comicFile, setComicFile] = useState(null);       // File obj for upload mode
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState('');
  const [jobId, setJobId] = useState('');
  const [jobStatus, setJobStatus] = useState('');
  const [completedVideo, setCompletedVideo] = useState(null);
  const [existingLessons, setExistingLessons] = useState([]);
  const [lessonsLoading, setLessonsLoading] = useState(false);
  const [deletingId, setDeletingId] = useState('');
  const pollTimerRef = useRef(null);
  const fileInputRef = useRef(null);
  const videoInputRef = useRef(null);

  const fetchExistingLessons = async () => {
    setLessonsLoading(true);
    try {
      const token = localStorage.getItem('token');
      const resp = await fetch(`${API_URL}/api/admin/video-lessons/all`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (resp.ok) setExistingLessons(await resp.json());
    } catch (_) { /* ignore */ }
    setLessonsLoading(false);
  };

  useEffect(() => {
    fetchExistingLessons();
    return () => { if (pollTimerRef.current) clearInterval(pollTimerRef.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [completedVideo]);

  const handleDeleteLesson = async (lesson) => {
    if (!window.confirm(`Delete "${lesson.title}"? This removes the MP4 from storage and the card from the student page. This cannot be undone.`)) return;
    setDeletingId(lesson.id);
    try {
      const token = localStorage.getItem('token');
      const resp = await fetch(`${API_URL}/api/admin/video-lessons/${lesson.id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (!resp.ok) throw new Error('Delete failed');
      await fetchExistingLessons();
    } catch (err) {
      setError(err.message);
    }
    setDeletingId('');
  };

  const handleFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!f.type.startsWith('image/')) {
      setError('Please upload an image file (PNG/JPG).');
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = reader.result;
      setComicPreview(dataUrl);
      setComicBase64(String(dataUrl).split(',')[1] || '');
    };
    reader.readAsDataURL(f);
  };

  const clearComic = () => {
    setComicPreview('');
    setComicBase64('');
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const startPolling = (id, token) => {
    pollTimerRef.current = setInterval(async () => {
      try {
        const resp = await fetch(`${API_URL}/api/admin/video-lessons/status/${id}`, {
          headers: { 'Authorization': `Bearer ${token}` },
        });
        if (!resp.ok) return;
        const data = await resp.json();
        setJobStatus(data.status);
        if (data.status === 'completed') {
          clearInterval(pollTimerRef.current);
          pollTimerRef.current = null;
          setCompletedVideo(data);
          setGenerating(false);
        } else if (data.status === 'failed') {
          clearInterval(pollTimerRef.current);
          pollTimerRef.current = null;
          setError(data.error_message || 'Video generation failed.');
          setGenerating(false);
        }
      } catch (_) { /* ignore transient errors */ }
    }, POLL_INTERVAL_MS);
  };

  const handleGenerate = async (e) => {
    e.preventDefault();
    setError('');
    setCompletedVideo(null);
    setJobId('');
    setJobStatus('pending');
    setGenerating(true);

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/admin/video-lessons/generate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          title,
          topic,
          prompt,
          comic_image_url: comicUrl || null,
          comic_image_base64: comicBase64 || null,
        }),
      });
      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.detail || 'Failed to start video generation');
      }
      const data = await response.json();
      setJobId(data.job_id);
      setJobStatus(data.status);
      startPolling(data.job_id, token);
    } catch (err) {
      setError(err.message);
      setGenerating(false);
    }
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    setError('');
    setCompletedVideo(null);
    setGenerating(true);
    try {
      if (!videoFile) throw new Error('Please choose a video file (MP4).');
      const token = localStorage.getItem('token');
      const fd = new FormData();
      fd.append('title', title);
      fd.append('topic', topic);
      fd.append('video', videoFile);
      if (comicFile) fd.append('comic', comicFile);
      const response = await fetch(`${API_URL}/api/admin/video-lessons/upload`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: fd,
      });
      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.detail || 'Upload failed');
      }
      const data = await response.json();
      setCompletedVideo(data);
      setGenerating(false);
    } catch (err) {
      setError(err.message);
      setGenerating(false);
    }
  };

  const resetForm = () => {
    setTitle(''); setTopic(''); setPrompt(''); setComicUrl('');
    clearComic();
    setVideoFile(null); setVideoFileName(''); setComicFile(null);
    if (videoInputRef.current) videoInputRef.current.value = '';
    setJobId(''); setJobStatus(''); setCompletedVideo(null); setError('');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-pink-50 to-blue-50">
      {/* Header */}
      <div className="bg-white/80 backdrop-blur-sm border-b border-purple-200 sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center gap-4">
          <button
            onClick={() => navigate('/dashboard')}
            className="p-2 hover:bg-purple-100 rounded-lg transition-colors"
            data-testid="back-to-dashboard-btn"
          >
            <ArrowLeft className="w-5 h-5 text-purple-600" />
          </button>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-pink-500 rounded-xl flex items-center justify-center">
              <Video className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-slate-900">Video Lesson Studio</h1>
              <p className="text-sm text-slate-600">Admin Only · Sora 2 or Upload</p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="bg-white rounded-2xl shadow-xl border border-purple-100 overflow-hidden">
          <div className="bg-gradient-to-r from-purple-500 to-pink-500 px-6 py-4">
            <h2 className="text-xl font-bold text-white flex items-center gap-2">
              {mode === 'upload' ? <><FileVideo className="w-5 h-5" />Publish an Existing Video Lesson</> : <><Sparkles className="w-5 h-5" />Generate Video Lesson with Sora 2</>}
            </h2>
            <p className="text-purple-100 text-sm mt-1">
              {mode === 'upload'
                ? 'Upload an MP4 that\'s already been rendered — great for re-publishing preview-generated lessons on production.'
                : 'Upload a comic strip and turn it into an 8-second animated lesson.'}
            </p>
          </div>

          {completedVideo ? (
            <div className="p-6 space-y-4" data-testid="completed-video-panel">
              <div className="flex items-center gap-2 text-green-700 bg-green-50 border border-green-200 rounded-xl p-4">
                <CheckCircle className="w-5 h-5" />
                <span className="font-semibold">Video ready! Now live on the student Video Lessons page.</span>
              </div>
              <video
                src={completedVideo.video_url?.startsWith('http') ? completedVideo.video_url : `${API_URL}${completedVideo.video_url}`}
                controls
                autoPlay
                className="w-full rounded-xl border border-purple-200"
                data-testid="admin-preview-video"
              />
              <div className="flex gap-3">
                <button
                  onClick={() => navigate('/video-lessons')}
                  className="flex-1 bg-gradient-to-r from-purple-500 to-pink-500 text-white font-bold py-3 rounded-xl hover:shadow-lg transition-all"
                  data-testid="view-in-lessons-btn"
                >
                  View in Video Lessons
                </button>
                <button
                  onClick={resetForm}
                  className="flex-1 bg-white border-2 border-purple-300 text-purple-700 font-bold py-3 rounded-xl hover:bg-purple-50 transition-all"
                  data-testid="generate-another-btn"
                >
                  Generate Another
                </button>
              </div>
            </div>
          ) : (
            <form onSubmit={mode === 'generate' ? handleGenerate : handleUpload} className="p-6 space-y-6">
              {/* Mode Toggle */}
              <div className="grid grid-cols-2 gap-2 p-1 bg-purple-50 rounded-xl border border-purple-200" data-testid="mode-toggle">
                <button
                  type="button"
                  onClick={() => setMode('generate')}
                  disabled={generating}
                  className={`flex items-center justify-center gap-2 py-2.5 rounded-lg font-semibold text-sm transition-all ${mode === 'generate' ? 'bg-white shadow text-purple-700' : 'text-slate-500 hover:text-purple-700'}`}
                  data-testid="mode-generate-btn"
                >
                  <Sparkles className="w-4 h-4" /> AI Generate (Sora 2)
                </button>
                <button
                  type="button"
                  onClick={() => setMode('upload')}
                  disabled={generating}
                  className={`flex items-center justify-center gap-2 py-2.5 rounded-lg font-semibold text-sm transition-all ${mode === 'upload' ? 'bg-white shadow text-purple-700' : 'text-slate-500 hover:text-purple-700'}`}
                  data-testid="mode-upload-btn"
                >
                  <FileVideo className="w-4 h-4" /> Upload Existing MP4
                </button>
              </div>
              {mode === 'upload' && (
                <div className="text-xs text-slate-600 bg-blue-50 border border-blue-200 rounded-lg p-3">
                  💡 Use this to publish a video that's already been rendered (e.g., generated earlier in the preview environment). This skips Sora 2 and saves you 5 minutes + credits.
                </div>
              )}

              {/* Title */}
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">Lesson Title</label>
                <input
                  type="text"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder='e.g., "Everyone IS - Grammar Lesson"'
                  required
                  disabled={generating}
                  className="w-full px-4 py-3 border-2 border-purple-200 rounded-xl focus:border-purple-500 focus:ring-2 focus:ring-purple-200 transition-all disabled:opacity-50"
                  data-testid="video-title-input"
                />
              </div>

              {/* Topic */}
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">Topic / Grammar Rule</label>
                <input
                  type="text"
                  value={topic}
                  onChange={(e) => setTopic(e.target.value)}
                  placeholder='e.g., "Subject-Verb Agreement"'
                  required
                  disabled={generating}
                  className="w-full px-4 py-3 border-2 border-purple-200 rounded-xl focus:border-purple-500 focus:ring-2 focus:ring-purple-200 transition-all disabled:opacity-50"
                  data-testid="video-topic-input"
                />
              </div>

              {/* Comic Upload */}
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">
                  {mode === 'upload' ? 'Thumbnail / Comic Strip (Optional)' : 'Comic Strip (Optional but recommended)'}
                </label>
                {comicPreview ? (
                  <div className="relative rounded-xl overflow-hidden border-2 border-purple-200 bg-purple-50">
                    <img src={comicPreview} alt="Comic preview" className="w-full max-h-64 object-contain" data-testid="comic-preview" />
                    <button
                      type="button"
                      onClick={clearComic}
                      disabled={generating}
                      className="absolute top-2 right-2 bg-white/90 hover:bg-white text-red-600 rounded-lg px-3 py-1 text-sm font-semibold shadow"
                      data-testid="remove-comic-btn"
                    >
                      Remove
                    </button>
                  </div>
                ) : (
                  <label className="flex flex-col items-center justify-center border-2 border-dashed border-purple-300 rounded-xl p-6 cursor-pointer hover:bg-purple-50 transition-all" data-testid="comic-drop-zone">
                    <Upload className="w-8 h-8 text-purple-400 mb-2" />
                    <span className="text-sm font-semibold text-purple-700">Click to upload comic strip</span>
                    <span className="text-xs text-slate-500 mt-1">
                      {mode === 'upload'
                        ? 'PNG/JPG · used as the card thumbnail on the student page'
                        : 'PNG/JPG · GPT-5.2 Vision reads every panel to guide Sora 2'}
                    </span>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*"
                      onChange={(e) => { handleFile(e); if (mode === 'upload') setComicFile(e.target.files?.[0] || null); }}
                      disabled={generating}
                      className="hidden"
                      data-testid="comic-file-input"
                    />
                  </label>
                )}
                {mode === 'generate' && (
                  <div className="mt-3">
                    <label className="block text-xs font-semibold text-slate-500 mb-1">Or paste an image URL</label>
                    <input
                      type="url"
                      value={comicUrl}
                      onChange={(e) => setComicUrl(e.target.value)}
                      placeholder="https://example.com/comic.png"
                      disabled={generating}
                      className="w-full px-3 py-2 text-sm border border-purple-200 rounded-lg focus:border-purple-500 focus:ring-1 focus:ring-purple-200 transition-all disabled:opacity-50"
                      data-testid="comic-image-url-input"
                    />
                  </div>
                )}
              </div>

              {mode === 'generate' ? (
                /* Prompt */
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-2">Video Generation Prompt</label>
                  <textarea
                    value={prompt}
                    onChange={(e) => setPrompt(e.target.value)}
                    placeholder="Describe the video content in detail (voiceover, characters, teaching narrative). If a comic is uploaded, this is layered on top of what Sora sees."
                    required
                    disabled={generating}
                    rows={7}
                    className="w-full px-4 py-3 border-2 border-purple-200 rounded-xl focus:border-purple-500 focus:ring-2 focus:ring-purple-200 transition-all font-mono text-sm disabled:opacity-50"
                    data-testid="video-prompt-input"
                  />
                  <p className="text-xs text-slate-500 mt-1">Video duration: 8 seconds · 1280×720 · sora-2</p>
                </div>
              ) : (
                /* Video File Upload */
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-2">Video File (MP4)</label>
                  <label className="flex flex-col items-center justify-center border-2 border-dashed border-purple-300 rounded-xl p-6 cursor-pointer hover:bg-purple-50 transition-all" data-testid="video-drop-zone">
                    <FileVideo className="w-8 h-8 text-purple-400 mb-2" />
                    <span className="text-sm font-semibold text-purple-700">{videoFileName || 'Click to choose an MP4 file'}</span>
                    <span className="text-xs text-slate-500 mt-1">Max 100 MB · H.264 MP4 recommended</span>
                    <input
                      ref={videoInputRef}
                      type="file"
                      accept="video/mp4,video/quicktime"
                      required
                      onChange={(e) => {
                        const f = e.target.files?.[0];
                        if (!f) return;
                        if (f.size > 100 * 1024 * 1024) { setError('Video must be under 100 MB.'); return; }
                        setError('');
                        setVideoFile(f); setVideoFileName(f.name);
                      }}
                      disabled={generating}
                      className="hidden"
                      data-testid="video-file-input"
                    />
                  </label>
                  <p className="text-xs text-slate-500 mt-1">
                    Tip: to migrate a preview-generated lesson to production, download the MP4 from the preview <em>/api/video-lessons/&#123;id&#125;/media</em> URL and upload it here.
                  </p>
                </div>
              )}

              {error && (
                <div className="p-4 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm flex items-start gap-2" data-testid="error-message">
                  <XCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
                  <span>{error}</span>
                </div>
              )}

              {/* Progress panel */}
              {generating && (
                <div className="p-4 bg-purple-50 border border-purple-200 rounded-xl space-y-2" data-testid="progress-panel">
                  <div className="flex items-center gap-3 text-purple-700 font-semibold">
                    <Loader2 className="w-5 h-5 animate-spin" />
                    {mode === 'upload'
                      ? 'Uploading video to storage…'
                      : (jobStatus === 'processing' ? 'Sora 2 is rendering your video…' : 'Queued — starting up…')}
                  </div>
                  {mode === 'generate' && (
                    <p className="text-xs text-slate-600">
                      Job ID: <span className="font-mono">{jobId || '—'}</span>. This usually takes 2–5 minutes. You can leave this page — the video will appear in <button type="button" onClick={() => navigate('/video-lessons')} className="underline text-purple-700 font-semibold">Video Lessons</button> when done.
                    </p>
                  )}
                </div>
              )}

              <button
                type="submit"
                disabled={generating}
                className="w-full bg-gradient-to-r from-purple-500 to-pink-500 text-white font-bold py-4 px-6 rounded-xl hover:shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-3"
                data-testid="generate-video-btn"
              >
                {generating ? (
                  <><Loader2 className="w-5 h-5 animate-spin" />{mode === 'upload' ? 'Uploading…' : 'Generating… (polling every 5s)'}</>
                ) : mode === 'upload' ? (
                  <><FileVideo className="w-5 h-5" />Publish Video Lesson</>
                ) : (
                  <><Sparkles className="w-5 h-5" />Generate Video Lesson</>
                )}
              </button>
            </form>
          )}
        </div>

        {/* Quick Example */}
        <div className="mt-6 p-4 bg-purple-50 rounded-xl border border-purple-200">
          <h3 className="font-bold text-slate-800 mb-2 flex items-center gap-2"><ImageIcon className="w-4 h-4" /> Tip</h3>
          <p className="text-sm text-slate-700 italic">
            {mode === 'upload'
              ? 'To copy a video from preview to production: download it from the preview /api/video-lessons/{id}/media URL, then upload the MP4 here — no extra Sora 2 credits used.'
              : 'Upload your 6-frame comic strip and add a short prompt. GPT-5.2 Vision will read every panel and turn it into a cinematic Sora 2 scene — no need to manually describe each frame.'}
          </p>
        </div>

        {/* Manage Existing Lessons */}
        <div className="mt-6 bg-white rounded-2xl shadow-xl border border-purple-100 overflow-hidden" data-testid="manage-lessons-panel">
          <div className="bg-gradient-to-r from-slate-800 to-slate-700 px-6 py-4 flex items-center justify-between">
            <div>
              <h3 className="text-lg font-bold text-white flex items-center gap-2"><Video className="w-5 h-5" /> Manage Existing Video Lessons</h3>
              <p className="text-slate-300 text-xs mt-0.5">Everything stored on this environment — delete removes it from storage AND the student page.</p>
            </div>
            <button
              type="button"
              onClick={fetchExistingLessons}
              disabled={lessonsLoading}
              className="text-xs font-semibold bg-white/10 hover:bg-white/20 text-white px-3 py-1.5 rounded-lg disabled:opacity-50"
              data-testid="refresh-lessons-btn"
            >
              {lessonsLoading ? 'Refreshing…' : 'Refresh'}
            </button>
          </div>
          <div className="divide-y divide-purple-100">
            {existingLessons.length === 0 && !lessonsLoading && (
              <div className="p-8 text-center text-sm text-slate-500" data-testid="no-lessons-msg">
                No video lessons on this environment yet. Generate one above, or switch to Upload Existing MP4 to publish a preview-rendered lesson here.
              </div>
            )}
            {existingLessons.map((lesson) => {
              const statusStyle = {
                completed: 'bg-green-100 text-green-700',
                processing: 'bg-amber-100 text-amber-700',
                pending: 'bg-slate-100 text-slate-600',
                failed: 'bg-red-100 text-red-700',
              }[lesson.status] || 'bg-slate-100 text-slate-600';
              return (
                <div key={lesson.id} className="p-4 flex items-center gap-4 hover:bg-purple-50/40" data-testid={`lesson-row-${lesson.id}`}>
                  <div className="w-16 h-16 rounded-lg bg-gradient-to-br from-purple-100 to-pink-100 flex-shrink-0 overflow-hidden flex items-center justify-center">
                    {lesson.comic_image_url ? (
                      <img src={lesson.comic_image_url.startsWith('http') ? lesson.comic_image_url : `${API_URL}${lesson.comic_image_url}`} alt="" className="w-full h-full object-cover" />
                    ) : (
                      <Video className="w-6 h-6 text-purple-400" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h4 className="font-bold text-slate-900 truncate">{lesson.title}</h4>
                      <span className={`text-[10px] font-black uppercase px-2 py-0.5 rounded-full ${statusStyle}`}>{lesson.status}</span>
                    </div>
                    <p className="text-xs text-slate-600 truncate">{lesson.topic || '—'}</p>
                    <p className="text-[11px] text-slate-400 mt-1 flex items-center gap-3">
                      <span className="inline-flex items-center gap-1"><Eye className="w-3 h-3" /> {lesson.view_count} views</span>
                      <span className="font-mono">{lesson.id.slice(0, 8)}…</span>
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => handleDeleteLesson(lesson)}
                    disabled={deletingId === lesson.id}
                    className="inline-flex items-center gap-1.5 text-sm font-semibold text-red-600 hover:text-white hover:bg-red-600 border border-red-200 hover:border-red-600 px-3 py-2 rounded-lg transition-all disabled:opacity-50"
                    data-testid={`delete-lesson-${lesson.id}`}
                  >
                    {deletingId === lesson.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                    Delete
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
